import { useState } from 'react';
import { TrendingUp, TrendingDown, DollarSign, ShoppingCart, Wallet } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@/components/ui/Table';
import { TabVendas } from './components/TabVendas';

export function RelatoriosPage() {
  const [tab, setTab] = useState<'financeiro' | 'vendas' | 'caixas'>('financeiro');
  const [dataInicio, setDataInicio] = useState(() => {
    const date = new Date();
    date.setMonth(date.getMonth() - 3); // Trimestral por padrão
    return date.toISOString().split('T')[0] ?? '';
  });
  const [dataFim, setDataFim] = useState(() => new Date().toISOString().split('T')[0] ?? '');

  const { data: resumo, isLoading } = trpc.dashboard.resumoFinanceiro.useQuery({
    dataInicio: dataInicio ? new Date(dataInicio) : undefined,
    dataFim: dataFim ? new Date(dataFim) : undefined,
  });

  const { data: caixasHistorico } = trpc.caixas.getHistorico.useQuery({
    dataInicio: dataInicio ? new Date(dataInicio) : undefined,
    dataFim: dataFim ? new Date(dataFim) : undefined,
    page: 1,
    limit: 50,
  });

  const formatCurrency = (value: string | number | null | undefined) => {
    if (!value) return 'R$ 0,00';
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(num);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Relatórios</h1>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b">
        <button
          onClick={() => setTab('financeiro')}
          className={`px-4 py-2 font-medium transition-colors ${
            tab === 'financeiro'
              ? 'border-primary-600 text-primary-600 border-b-2'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <div className="flex items-center gap-2">
            <DollarSign className="h-4 w-4" />
            Financeiro
          </div>
        </button>
        <button
          onClick={() => setTab('vendas')}
          className={`px-4 py-2 font-medium transition-colors ${
            tab === 'vendas'
              ? 'border-primary-600 text-primary-600 border-b-2'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <div className="flex items-center gap-2">
            <ShoppingCart className="h-4 w-4" />
            Vendas
          </div>
        </button>
        <button
          onClick={() => setTab('caixas')}
          className={`px-4 py-2 font-medium transition-colors ${
            tab === 'caixas'
              ? 'border-primary-600 text-primary-600 border-b-2'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <div className="flex items-center gap-2">
            <Wallet className="h-4 w-4" />
            Caixas
          </div>
        </button>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Data Início</label>
              <input
                type="date"
                value={dataInicio}
                onChange={(e) => setDataInicio(e.target.value)}
                className="focus:border-primary-500 focus:ring-primary-500 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-1"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Data Fim</label>
              <input
                type="date"
                value={dataFim}
                onChange={(e) => setDataFim(e.target.value)}
                className="focus:border-primary-500 focus:ring-primary-500 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-1"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <div className="border-primary-600 h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
        </div>
      ) : tab === 'financeiro' ? (
        <>
          {/* Cards de Resumo Financeiro */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-500">Receitas</p>
                    <p className="text-2xl font-bold text-green-600">
                      {formatCurrency(resumo?.totalReceitas)}
                    </p>
                  </div>
                  <div className="rounded-full bg-green-100 p-3">
                    <TrendingUp className="h-6 w-6 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-500">Despesas</p>
                    <p className="text-2xl font-bold text-red-600">
                      {formatCurrency(resumo?.totalDespesas)}
                    </p>
                  </div>
                  <div className="rounded-full bg-red-100 p-3">
                    <TrendingDown className="h-6 w-6 text-red-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-500">Saldo</p>
                    <p
                      className={`text-2xl font-bold ${(resumo?.saldo ?? 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}
                    >
                      {formatCurrency(resumo?.saldo)}
                    </p>
                  </div>
                  <div className="rounded-full bg-blue-100 p-3">
                    <DollarSign className="h-6 w-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-500">Contas</p>
                    <p className="text-primary-600 text-2xl font-bold">
                      {resumo?.contasPorStatus.pendentes ?? 0} pendentes
                    </p>
                  </div>
                  <div className="bg-primary-100 rounded-full p-3">
                    <ShoppingCart className="text-primary-600 h-6 w-6" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Detalhamento */}
          <div className="grid gap-6 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Contas a Receber</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Receitas</span>
                    <span className="font-medium text-green-600">
                      {formatCurrency(resumo?.totalReceitas)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Pendentes</span>
                    <span className="font-medium text-amber-600">
                      {formatCurrency(resumo?.receitasPendentes)}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Contas a Pagar</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Despesas</span>
                    <span className="font-medium text-red-600">
                      {formatCurrency(resumo?.totalDespesas)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Pendentes</span>
                    <span className="font-medium text-amber-600">
                      {formatCurrency(resumo?.despesasPendentes)}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </>
      ) : tab === 'vendas' ? (
        <TabVendas dataInicio={dataInicio} dataFim={dataFim} />
      ) : (
        <>
          {/* Histórico de sessões de caixa — conferência, não vendas */}
          <Card>
            <CardHeader>
              <CardTitle>Histórico de Caixas</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Data Abertura</TableHead>
                    <TableHead>Data Fechamento</TableHead>
                    <TableHead>Valor Abertura</TableHead>
                    <TableHead>Valor Fechamento</TableHead>
                    <TableHead>Diferença</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {caixasHistorico?.items.map((caixa) => {
                    const diferenca = caixa.valorFechamento
                      ? parseFloat(caixa.valorFechamento) - parseFloat(caixa.valorAbertura)
                      : 0;
                    return (
                      <TableRow key={caixa.id}>
                        <TableCell>
                          {new Date(caixa.dataAbertura).toLocaleString('pt-BR')}
                        </TableCell>
                        <TableCell>
                          {caixa.dataFechamento
                            ? new Date(caixa.dataFechamento).toLocaleString('pt-BR')
                            : '-'}
                        </TableCell>
                        <TableCell>{formatCurrency(caixa.valorAbertura)}</TableCell>
                        <TableCell>
                          {caixa.valorFechamento ? formatCurrency(caixa.valorFechamento) : '-'}
                        </TableCell>
                        <TableCell>
                          {caixa.valorFechamento ? (
                            <span className={diferenca >= 0 ? 'text-green-600' : 'text-red-600'}>
                              {diferenca >= 0 ? '+' : ''}
                              {formatCurrency(diferenca)}
                            </span>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell>
                          <span
                            className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${
                              caixa.status === 'ABERTO'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {caixa.status === 'ABERTO' ? 'Aberto' : 'Fechado'}
                          </span>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

export default RelatoriosPage;
